import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Category,
  listCategories as apiListCategories
} from '../../../../../services/category'
import {
  Person,
  listPeople as apiListCustomers
} from '../../../../../services/person'
import {
  Signature,
  SignatureRecurrence,
  getSignature as apiGetSignature
} from '../../../../../services/signature'
import {
  SignatureFormatted,
  formatSignature
} from '../../../utils/formatSignature'

interface SignatureFormContextContent {
  signature?: SignatureFormatted
  loading: boolean
  setLoading: (loading: boolean) => void
  getSignatureState: () => SignatureFormatted
  updateSignatureState: (signature: Signature) => void
  localChangeRecurrence: (recurrence: string) => void
  categories: Category[]
  loadingCategories: boolean
  listCategories: () => void
  customers: Person[]
  loadingCustomers: boolean
  listCustomers: (search: string) => void
}

export const SignatureFormContext =
  React.createContext<SignatureFormContextContent>(
    {} as SignatureFormContextContent
  )

interface SignatureFormProviderProps {
  children: React.ReactNode
}

export const SignatureFormProvider: React.FC<SignatureFormProviderProps> = ({
  children
}) => {
  const [searchParams] = useSearchParams()
  const [signature, setSignature] = useState<SignatureFormatted>()
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [customers, setCustomers] = useState<Person[]>([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)

  function updateSignatureState(data: Signature) {
    const formatted = formatSignature(data)
    setSignature(formatted)
  }

  function getSignature(id: string) {
    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => apiGetSignature(id))
      .then(data => data && updateSignatureState(data))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const id = searchParams.get('id')
    if (id) getSignature(id)
  }, [searchParams])

  function getSignatureState() {
    if (!signature) throw new Error('Assinatura não encontrada')
    return signature
  }

  function localChangeRecurrence(recurrence: string) {
    if (!signature) return

    setSignature({
      ...signature,
      recurrence: recurrence as SignatureRecurrence
    })
  }

  function listCategories() {
    Promise.resolve()
      .then(() => setLoadingCategories(true))
      .then(() => apiListCategories())
      .then(data => setCategories(data.categories))
      .finally(() => setLoadingCategories(false))
  }

  function listCustomers(search: string) {
    const searchBy = ['document', 'name']

    Promise.resolve()
      .then(() => setLoadingCustomers(true))
      .then(() => apiListCustomers({ search, searchBy }))
      .then(data => setCustomers(data.people))
      .finally(() => setLoadingCustomers(false))
  }

  return (
    <SignatureFormContext.Provider
      value={{
        signature,
        loading,
        setLoading,
        getSignatureState,
        updateSignatureState,
        localChangeRecurrence,
        categories,
        loadingCategories,
        listCategories,
        customers,
        loadingCustomers,
        listCustomers
      }}
    >
      {children}
    </SignatureFormContext.Provider>
  )
}
